/**
 * HotelCancelModal — 2-click cancellation confirmation with fee preview.
 *
 * Never submits without an explicit second click. Fetches the
 * current cancellation fee via `/api/hotels/bookings/{ref}/cancel-preview`
 * so the amount shown to the user is what actually gets applied.
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

export default function HotelCancelModal({ reference, hotelName, onClose, onCancelled }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/hotels/bookings/${encodeURIComponent(reference)}/cancel-preview`)
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch((e) => { if (alive) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reference]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      const { data } = await api.post(
        `/hotels/bookings/${encodeURIComponent(reference)}/cancel`,
        { confirm: true },
        { timeout: 90000 },
      );
      if (onCancelled) onCancelled(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Cancellation failed. Please try again or contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  const fee = preview?.cancellation_fee;
  const currency = preview?.currency;
  const isFullRefund = fee === 0 || fee === null || fee === undefined;

  return (
    <div
      data-testid="hotel-cancel-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tb-navy-deep/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white border-2 border-tb-cream-3 shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" strokeWidth={2} />
            <h2 className="italic text-tb-navy text-xl leading-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
              Cancel booking?
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-1 -m-1 text-tb-navy/50 hover:text-tb-navy tb-tap"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="mt-4 font-hand text-tb-navy text-base">
          You are about to cancel your booking at{" "}
          <span className="font-medium">{hotelName || preview?.hotel_name || "your hotel"}</span>.
        </p>

        {loading ? (
          <p className="mt-4 font-hand text-tb-navy/60 italic">Fetching latest cancellation fee…</p>
        ) : preview ? (
          preview.is_non_refundable ? (
            <div data-testid="cancel-modal-non-refundable" className="mt-5 rounded-xl border-2 border-red-300 bg-red-50 p-4">
              <div className="font-hand text-red-800 text-[11px] uppercase tracking-widest">Non-refundable rate</div>
              <p className="mt-1 font-hand text-tb-navy text-sm">
                This rate is non-refundable. Cancelling now will result in a full charge of{" "}
                <b>{preview.currency} {preview.cancellation_fee}</b>.
              </p>
            </div>
          ) : preview.cancellation_fee > 0 ? (
            <div data-testid="cancel-modal-paid" className="mt-5 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
              <div className="font-hand text-amber-800 text-[11px] uppercase tracking-widest">Cancelling now</div>
              <div data-testid="cancel-modal-fee" className="mt-1 font-serif text-tb-navy text-2xl" style={{ fontFamily: '"Playfair Display", serif' }}>
                {preview.currency} {preview.cancellation_fee}
              </div>
              <p className="mt-2 font-hand text-tb-navy/75 text-xs">
                The free cancellation window for this booking has ended. This amount will be charged.
              </p>
            </div>
          ) : (
            <div data-testid="cancel-modal-full-refund-wrap" className="mt-5 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
              <div data-testid="cancel-modal-full-refund" className="font-serif text-emerald-800 text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>
                You still have free cancellation
              </div>
              {preview.cancellation_deadline_display ? (
                <p data-testid="cancel-modal-deadline" className="mt-2 font-hand text-tb-navy text-sm">
                  Free cancellation ends <b>{preview.cancellation_deadline_display}</b>.
                </p>
              ) : (
                <p className="mt-2 font-hand text-tb-navy text-sm">
                  You&apos;ll be fully refunded if you cancel now.
                </p>
              )}
              {preview.next_policy_step && (
                <p data-testid="cancel-modal-next-step" className="mt-1.5 font-hand text-tb-navy/70 text-xs">
                  After that, a cancellation fee of <b>{preview.currency} {preview.next_policy_step.fee}</b> applies.
                </p>
              )}
            </div>
          )
        ) : null}
        {preview?.destination_timezone && (
          <div className="mt-2 font-hand text-tb-navy/45 text-[10px] text-right">
            All times shown in destination local time zone: {preview.destination_timezone}
          </div>
        )}

        {err && (
          <div data-testid="hotel-cancel-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">
            {err}
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid="hotel-cancel-keep"
            onClick={onClose}
            className="flex-1 px-5 py-2.5 rounded-full bg-white text-tb-navy border-2 border-tb-cream-3 font-hand text-base tb-tap hover:border-tb-navy"
          >
            Keep booking
          </button>
          <button
            type="button"
            data-testid="hotel-cancel-confirm"
            onClick={submit}
            disabled={submitting || loading || preview?.already_cancelled}
            className="flex-1 px-5 py-2.5 rounded-full bg-red-700 text-white border-2 border-red-800 font-hand text-base tb-tap hover:brightness-110 disabled:opacity-45"
          >
            {submitting ? "~ cancelling ~" : "Confirm cancellation"}
          </button>
        </div>

        <div className="mt-4 flex items-center justify-center">
          <PoweredByHotelbeds size="xs" />
        </div>
      </div>
    </div>
  );
}
